// Google Analytics event tracking utility

declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event',
      targetId: string,
      config?: {
        page_path?: string;
        event_category?: string;
        event_label?: string;
        value?: number;
        [key: string]: any;
      },
    ) => void;
    dataLayer?: Record<string, any>[];
  }
}

interface AnalyticsEvent {
  action: string;
  category?: string;
  label?: string;
  value?: number;
  [key: string]: any; // For custom parameters
}

export const trackGAEvent = (event: AnalyticsEvent): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    const { action, category, label, value, ...customParams } = event;

    // Callers track right after a successful API call, so a throw here would show up as a failed
    // save. Once Google's scripts load they take over dataLayer.push, so this runs their code.
    try {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
        ...customParams,
      });
    } catch (error) {
      console.error(`Failed to send GA event ${action}`, error);
    }
  }
};

/** Pushes a custom event to the dataLayer so GTM can trigger tags on it. gtag events are not visible to GTM triggers. */
export const pushDataLayerEvent = (event: string, params?: Record<string, any>): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // Same reason as in trackGAEvent: tracking must not break the caller's success path
  try {
    window.dataLayer.push({ event, ...params });
  } catch (error) {
    console.error(`Failed to push dataLayer event ${event}`, error);
  }
};

/** Sends a Lambda funnel event to GA (category onboarding) and to the GTM dataLayer, with the same params. */
export const trackLambdaEvent = (event: string, params?: Record<string, any>): void => {
  trackGAEvent({ action: event, category: 'onboarding', ...params });
  pushDataLayerEvent(event, params);
};
